import React, { useState, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { TrendingUp, TrendingDown, AlertCircle, Loader2 } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { usePriceHistory } from '../hooks/usePriceHistory';

const RANGE_OPTIONS = [
  { id: 'day',   label: '24H' },
  { id: 'week',  label: '1W' },
  { id: 'month', label: '1M' },
  { id: '3m',    label: '3M' },
  { id: '6m',    label: '6M' },
  { id: 'ytd',   label: 'YTD' },
  { id: '1y',    label: '1Y' },
  { id: '5y',    label: '5Y' },
];

/**
 * Price chart for a ticker on the Mentions detail page. Range buttons
 * across the top, sparkline + tooltip below, change-since-start summary.
 *
 * Hidden when the backend reports neither Schwab nor CoinGecko has data
 * for this symbol -- e.g. a synthetic alias like "QQQ-LIKE" the model
 * extracted but no provider trades.
 */
const TickerPriceChart = ({ symbol }) => {
  const [range, setRange] = useState('month');
  const { data, loading, error, unavailable } = usePriceHistory(symbol, range);

  const series = useMemo(() => {
    if (!data?.points) return [];
    return data.points.map((p) => ({
      time: parseISO(p.time).getTime(),
      timeLabel: p.time,
      close: Number(p.close),
    }));
  }, [data]);

  const summary = useMemo(() => {
    if (series.length < 2) return null;
    const first = series[0].close;
    const last  = series[series.length - 1].close;
    const change = last - first;
    const pct = first === 0 ? 0 : (change / first) * 100;
    return { first, last, change, pct };
  }, [series]);

  if (unavailable) return null; // Provider returned no data; hide silently.

  return (
    <div className="card" style={{ marginBottom: '1.25rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.75rem' }}>
        <h2 style={{
          fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-secondary)',
          textTransform: 'uppercase', letterSpacing: '0.05em',
          margin: 0, display: 'flex', alignItems: 'center', gap: '0.375rem',
        }}>
          Price action
          {data?.source && (
            <span style={{ fontSize: '0.6875rem', color: 'var(--text-secondary)', textTransform: 'none', letterSpacing: 0, fontWeight: 400 }}>
              via {data.source}
            </span>
          )}
        </h2>

        <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
          {RANGE_OPTIONS.map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => setRange(opt.id)}
              className={`btn ${range === opt.id ? 'btn-primary' : 'btn-secondary'}`}
              style={{ fontSize: '0.75rem', padding: '0.25rem 0.625rem' }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {summary && (
        <div style={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '0.75rem' }}>
          <span style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-primary)' }}>
            ${summary.last.toLocaleString(undefined, { maximumFractionDigits: 2 })}
          </span>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
            fontSize: '0.9375rem', fontWeight: 600,
            color: summary.change >= 0 ? 'var(--success-color)' : 'var(--danger-color)',
          }}>
            {summary.change >= 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
            {summary.change >= 0 ? '+' : ''}${Math.abs(summary.change).toLocaleString(undefined, { maximumFractionDigits: 2 })}
            ({summary.pct >= 0 ? '+' : ''}{summary.pct.toFixed(2)}%)
          </span>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
            over {RANGE_OPTIONS.find((o) => o.id === range)?.label}
          </span>
        </div>
      )}

      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '2rem', color: 'var(--text-secondary)' }}>
          <Loader2 size={16} className="spin" />
          Loading price...
        </div>
      ) : error ? (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '1rem',
          background: 'var(--severity-warn-bg)',
          borderLeft: '4px solid var(--severity-warn-border)',
          borderRadius: '4px', fontSize: '0.875rem',
        }}>
          <AlertCircle size={16} /> {error}
        </div>
      ) : series.length === 0 ? (
        <div style={{ padding: '1rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
          No price data available for this range.
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={series} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
            <XAxis
              dataKey="time"
              type="number"
              domain={['dataMin', 'dataMax']}
              tickFormatter={(t) => formatTickX(t, range)}
              stroke="var(--text-secondary)"
              style={{ fontSize: '0.75rem' }}
              minTickGap={40}
            />
            <YAxis
              domain={['auto', 'auto']}
              tickFormatter={(v) => `$${formatTickY(v)}`}
              stroke="var(--text-secondary)"
              style={{ fontSize: '0.75rem' }}
              width={56}
            />
            <Tooltip
              labelFormatter={(t) => format(new Date(t), 'EEE MMM dd, h:mm a')}
              formatter={(v) => [`$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`, 'Close']}
              contentStyle={{
                backgroundColor: 'var(--bg-primary)',
                border: '1px solid var(--border-color)',
                borderRadius: '6px',
                color: 'var(--text-primary)',
                fontSize: '0.8125rem',
              }}
            />
            {summary && (
              <ReferenceLine y={summary.first} stroke="var(--text-secondary)" strokeDasharray="3 3" strokeOpacity={0.4} />
            )}
            <Line
              type="monotone"
              dataKey="close"
              stroke={summary && summary.change >= 0 ? 'var(--success-color)' : 'var(--danger-color)'}
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

const formatTickX = (t, range) => {
  const d = new Date(t);
  if (range === 'day') return format(d, 'h:mm a');
  if (range === 'week') return format(d, 'EEE h a');
  // For >= 1 year, drop the day and show month + year so ticks aren't ambiguous.
  if (range === '1y' || range === '5y') return format(d, 'MMM yy');
  return format(d, 'MMM dd');
};

const formatTickY = (v) => {
  if (v >= 1000) return `${(v / 1000).toFixed(1)}k`;
  if (v >= 1) return v.toFixed(0);
  return v.toFixed(2);
};

export default TickerPriceChart;
